import { useEffect, useMemo, useRef, useState } from 'react';
import type { NewsMode, Segment, WordCloudEntry } from '../../types';
import SegmentToggle from '../common/SegmentToggle';
import NewsModeToggle from '../common/NewsModeToggle';
import WordCloud from 'wordcloud';
import Select, { type StylesConfig } from 'react-select';

interface WordCloudChartProps {
  data: Record<NewsMode, Record<Segment, WordCloudEntry[]>>;
  newsMode: NewsMode;
  onNewsModeChange: (mode: NewsMode) => void;
  locationOptions: string[];
  selectedLocation: string;
  onLocationChange: (location: string) => void;
}

const selectStyles: StylesConfig<{ value: string; label: string }, false> = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: 'rgba(8,11,22,0.85)',
    borderColor: state.isFocused ? 'rgba(159,178,255,0.8)' : 'rgba(255,255,255,0.12)',
    boxShadow: 'none',
    minHeight: 44,
    cursor: 'pointer',
    color: '#f6f9ff'
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: '#070b18',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 12,
    overflow: 'hidden'
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? 'rgba(124,134,255,0.35)'
      : state.isFocused
        ? 'rgba(124,134,255,0.15)'
        : 'transparent',
    color: '#f6f9ff',
    cursor: 'pointer'
  }),
  singleValue: (provided) => ({
    ...provided,
    color: '#f6f9ff'
  }),
  input: (provided) => ({
    ...provided,
    color: '#f6f9ff'
  }),
  placeholder: (provided) => ({
    ...provided,
    color: '#7c8bb6'
  })
};

const WordCloudChart = ({ 
  data, 
  newsMode, 
  onNewsModeChange,
  locationOptions,
  selectedLocation,
  onLocationChange
}: WordCloudChartProps) => {
  const [segment, setSegment] = useState<Segment>('Left');
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [canvasSize, setCanvasSize] = useState({ width: 800, height: 320 });

  const words = useMemo(() => {
    const entries = data[newsMode]?.[segment] ?? [];
    const sorted = [...entries].sort((a, b) => b.score - a.score).slice(0, 80);
    return sorted.map((word) => [word.term, word.score] as [string, number]);
  }, [data, newsMode, segment]);

  useEffect(() => {
    const updateSize = () => {
      if (!containerRef.current) return;
      const width = containerRef.current.offsetWidth;
      const height = Math.max(260, Math.min(460, width * 0.45));
      setCanvasSize({ width, height });
    };

    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  useEffect(() => {
    if (!canvasRef.current) return;
    if (words.length === 0) {
      const ctx = canvasRef.current.getContext('2d');
      if (ctx) ctx.clearRect(0, 0, canvasSize.width, canvasSize.height);
      return;
    }

    WordCloud(canvasRef.current, {
      list: words,
      gridSize: 12,
      weightFactor: (weight: number) => 12 + (weight / words[0][1]) * 24,
      backgroundColor: 'rgba(5,8,18,0.6)',
      color: () => {
        if (segment === 'Left') return '#ff6f8f';
        if (segment === 'Right') return '#6fa4ff';
        return '#c8d0e3';
      },
      rotateRatio: 0,
      shuffle: true,
      drawOutOfBound: false
    });

    return () => {
      WordCloud.stop();
    };
  }, [words, segment, canvasSize]);

  return (
    <div className="chart-wrapper">
      <div className="chart-controls">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Select
            className="location-select"
            classNamePrefix="select"
            styles={selectStyles}
            options={locationOptions.map((loc) => ({ value: loc, label: loc }))}
            value={{ value: selectedLocation, label: selectedLocation }}
            onChange={(opt) => opt && onLocationChange(opt.value)}
            placeholder="Search location..."
          />
          <SegmentToggle value={segment} onChange={setSegment} />
        </div>
        <div style={{ alignSelf: 'flex-end', marginBottom: '4px' }}>
          <NewsModeToggle value={newsMode} onChange={onNewsModeChange} />
        </div>
      </div>
      <div
        ref={containerRef}
        className="word-cloud__canvas"
        style={{ height: `${canvasSize.height}px` }}
      >
        <canvas
          ref={canvasRef}
          width={canvasSize.width}
          height={canvasSize.height}
          style={{ width: '100%', height: '100%' }}
        />
        {words.length === 0 && <p className="footnote">No keywords for this selection.</p>}
      </div>
    </div>
  );
};

export default WordCloudChart;
